/**
 * Planning data model, stored in a Yjs document so the same data works
 * offline (IndexedDB), in the desktop app, and in real-time collaboration.
 *
 * Y.Doc layout:
 *   meta:    Y.Map  { worldName, seed, createdAt }
 *   objects: Y.Map<id, Y.Map>  each object = PlanObject fields
 */
import * as Y from "yjs";
import { bounds, gridBlocks, pathGrid, polygonGrid, rectCorners, ringCount, type BlockGrid } from "./geometry.ts";

export * from "./geometry.ts";
export * from "./board.ts";
export * from "./layouts.ts";
export * from "./schematic.ts";
import type { LayoutObject, Template } from "./layouts.ts";
import { normalizeSourceUrl, parseSchematicRef, schematicDims, type SchematicRef } from "./schematic.ts";
import { CARD_SIZE, freeSpot, nextCardPosition, parseCard, type BoardCard } from "./board.ts";

export type PlanKind = "path" | "area" | "building" | "marker";

export interface HomeLocation {
  x: number;
  y: number;
  z: number;
  setBy?: string;
}
export type PlanStatus = "idea" | "planned" | "in_progress" | "done";

export interface PlanObject {
  id: string;
  kind: PlanKind;
  name: string;
  color: string; // #rrggbb
  status: PlanStatus;
  dimension: string; // "minecraft:overworld"
  /**
   * block coordinates (x, z); path = polyline, area = polygon, building = footprint polygon
   * (older buildings stored 2 opposite corners — see buildingPoints / migrate), marker = 1 point
   */
  points: [number, number][];
  /** base y (buildings/markers); paths & areas follow the terrain */
  y: number;
  /** path width in blocks */
  width: number;
  /** building height in blocks */
  height: number;
  /** building: blocks per storey (default 4) */
  storey?: number;
  /** block it's built from (paths/areas: the surface, buildings: the walls), e.g. "minecraft:gravel" */
  material?: string;
  /** objects created together (layout preset / template) share a group id and name */
  group?: string;
  groupName?: string;
  /** where it's from (e.g. the original post of a downloaded schematic) — http(s) only */
  sourceUrl?: string;
  /** building made from an imported schematic (only its hash, size and orientation — see schematic.ts) */
  schematic?: SchematicRef;
  notes: string;
  author: string;
  createdAt: number;
  updatedAt: number;
}

export const KIND_DEFAULTS: Record<PlanKind, Pick<PlanObject, "color" | "width" | "height" | "name">> = {
  path: { color: "#e8b04a", width: 3, height: 0, name: "Path" },
  area: { color: "#4ab0e8", width: 0, height: 0, name: "Area" },
  building: { color: "#e85a4a", width: 0, height: 8, name: "Building" },
  marker: { color: "#b04ae8", width: 0, height: 0, name: "Marker" },
};

export const STATUS_LABEL: Record<PlanStatus, string> = {
  idea: "Idea",
  planned: "Planned",
  in_progress: "In progress",
  done: "Done",
};

export function newId() {
  const a = new Uint8Array(9);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 14);
}

export class PlanStore {
  readonly doc: Y.Doc;
  readonly objects: Y.Map<Y.Map<unknown>>;
  readonly meta: Y.Map<unknown>;
  readonly undo: Y.UndoManager;
  /** pinboard cards of all objects (flat, keyed by card id) */
  readonly cards: Y.Map<Y.Map<unknown>>;

  constructor(doc: Y.Doc) {
    this.doc = doc;
    this.objects = doc.getMap("objects");
    this.meta = doc.getMap("meta");
    this.cards = doc.getMap("cards");
    this.undo = new Y.UndoManager([this.objects, this.cards], { captureTimeout: 400 });
  }

  list(dimension?: string): PlanObject[] {
    const out: PlanObject[] = [];
    this.objects.forEach((m) => {
      const o = m.toJSON() as PlanObject;
      if (!dimension || o.dimension === dimension) out.push(o);
    });
    return out.sort((a, b) => a.createdAt - b.createdAt);
  }

  get(id: string): PlanObject | undefined {
    return this.objects.get(id)?.toJSON() as PlanObject | undefined;
  }

  create(init: Partial<PlanObject> & Pick<PlanObject, "kind" | "points" | "dimension">): PlanObject {
    const d = KIND_DEFAULTS[init.kind];
    // numbering only when no name was given (a list() per object would make big layouts slow)
    const count = init.name === undefined ? this.list().filter((o) => o.kind === init.kind).length + 1 : 0;
    const now = Date.now();
    const o: PlanObject = {
      id: newId(),
      name: `${d.name} ${count}`,
      color: this.defaultColor(init.kind),
      status: "planned",
      y: 64,
      width: d.width,
      height: d.height,
      notes: "",
      author: "",
      createdAt: now,
      updatedAt: now,
      ...init,
    };
    this.doc.transact(() => {
      const m = new Y.Map<unknown>();
      for (const [k, v] of Object.entries(o)) m.set(k, v);
      this.objects.set(o.id, m);
    });
    return o;
  }

  // ---------- home location (shared by everyone in the room, one per dimension) ----------

  getHome(dimension: string): HomeLocation | null {
    const h = (this.meta.get("home") as Record<string, HomeLocation> | undefined)?.[dimension];
    return h && Number.isFinite(h.x) && Number.isFinite(h.z) ? h : null;
  }

  setHome(dimension: string, home: HomeLocation | null) {
    const all = { ...((this.meta.get("home") as Record<string, HomeLocation> | undefined) ?? {}) };
    if (home) all[dimension] = home; else delete all[dimension];
    this.meta.set("home", all);
  }

  // ---------- colours ----------

  /** colour for new objects of a kind (per-world default, shared with collaborators) */
  defaultColor(kind: PlanKind): string {
    const k = this.meta.get("kindColors") as Partial<Record<PlanKind, string>> | undefined;
    return k?.[kind] ?? KIND_DEFAULTS[kind].color;
  }

  setDefaultColor(kind: PlanKind, color: string) {
    const k = { ...((this.meta.get("kindColors") as Record<string, string> | undefined) ?? {}), [kind]: color };
    this.meta.set("kindColors", k);
  }

  /** recently used custom colours (shared palette of this world) */
  palette(): string[] {
    return ((this.meta.get("palette") as string[] | undefined) ?? []).slice();
  }

  rememberColor(color: string) {
    const c = color.toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(c)) return;
    const next = [c, ...this.palette().filter((x) => x !== c)].slice(0, 12);
    this.meta.set("palette", next);
  }

  // ---------- layouts & templates ----------

  /** Create many objects at once as one group (one undo step). */
  createGroup(objs: LayoutObject[], dimension: string, groupName: string, author = "", fallbackY = 64): PlanObject[] {
    const group = newId();
    const out: PlanObject[] = [];
    this.undo.stopCapturing();
    this.doc.transact(() => {
      for (const o of objs) {
        const extra = o as LayoutObject & { status?: PlanStatus; notes?: string; storey?: number; sourceUrl?: string; schematic?: SchematicRef };
        out.push(this.create({
          kind: o.kind, points: o.points, dimension, name: o.name, y: o.y ?? fallbackY, author, group, groupName,
          ...(o.width !== undefined ? { width: o.width } : {}),
          ...(o.height !== undefined ? { height: o.height } : {}),
          ...(o.material ? { material: o.material } : {}),
          ...(o.color ? { color: o.color } : {}),
          ...(extra.status ? { status: extra.status } : {}),
          ...(extra.notes ? { notes: extra.notes } : {}),
          ...(extra.storey ? { storey: extra.storey } : {}),
          ...(extra.sourceUrl ? { sourceUrl: extra.sourceUrl } : {}),
          ...(extra.schematic ? { schematic: extra.schematic } : {}),
        }));
      }
    });
    this.undo.stopCapturing();
    return out;
  }

  /** members of a group */
  groupMembers(group: string): PlanObject[] {
    return this.list().filter((o) => o.group === group);
  }

  /** this world's shared templates */
  templates(): Template[] {
    return ((this.meta.get("templates") as Template[] | undefined) ?? []).slice();
  }

  saveTemplate(t: Template) {
    this.meta.set("templates", [t, ...this.templates().filter((x) => x.id !== t.id)].slice(0, 100));
  }

  deleteTemplate(id: string) {
    this.meta.set("templates", this.templates().filter((x) => x.id !== id));
  }

  // ---------- materials ----------

  /** recently used block materials of this world (shared), newest first */
  recentMaterials(): string[] {
    return ((this.meta.get("materials") as string[] | undefined) ?? []).slice();
  }

  rememberMaterial(id: string) {
    if (!MATERIAL_RE.test(id)) return;
    this.meta.set("materials", [id, ...this.recentMaterials().filter((x) => x !== id)].slice(0, 12));
  }

  // ---------- bulk edits ----------

  updateMany(patches: { id: string; patch: Partial<PlanObject> }[]) {
    this.doc.transact(() => { for (const { id, patch } of patches) this.update(id, patch); });
  }

  removeMany(ids: string[]) {
    this.doc.transact(() => { for (const id of ids) { this.objects.delete(id); this.removeCardsOf(id); } });
  }

  /** Create copies of objects moved by (dx, dz) blocks. Returns the new objects. */
  duplicate(objs: PlanObject[], dx: number, dz: number, dimension?: string): PlanObject[] {
    const out: PlanObject[] = [];
    this.doc.transact(() => {
      for (const src of objs) {
        const now = Date.now();
        const o: PlanObject = {
          ...src,
          id: newId(),
          name: / \(copy\)$/.test(src.name) ? src.name : `${src.name} (copy)`,
          points: translatePoints(src.points, dx, dz),
          dimension: dimension ?? src.dimension,
          createdAt: now,
          updatedAt: now,
        };
        const m = new Y.Map<unknown>();
        for (const [k, v] of Object.entries(o)) m.set(k, v);
        this.objects.set(o.id, m);
        // the copy gets its own copy of the pinboard
        for (const c of this.listCards(src.id)) {
          const id = newId();
          this.cards.set(id, cardMap({ ...c, id, objectId: o.id, createdAt: now, updatedAt: now }));
        }
        out.push(o);
      }
    });
    return out;
  }

  update(id: string, patch: Partial<PlanObject>) {
    const m = this.objects.get(id);
    if (!m) return;
    this.doc.transact(() => {
      // undefined removes the field (e.g. material "none", an emptied source link)
      for (const [k, v] of Object.entries(patch)) if (k !== "id") { if (v === undefined) m.delete(k); else m.set(k, v); }
      m.set("updatedAt", Date.now());
    });
  }

  remove(id: string) {
    this.doc.transact(() => { this.objects.delete(id); this.removeCardsOf(id); });
  }

  // ---------- pinboard ----------

  /** Cards of one object (or all), in stacking order. */
  listCards(objectId?: string): BoardCard[] {
    const out: BoardCard[] = [];
    this.cards.forEach((m) => {
      const c = m.toJSON() as BoardCard;
      if (!objectId || c.objectId === objectId) out.push(c);
    });
    return out.sort((a, b) => a.z - b.z || a.createdAt - b.createdAt);
  }

  getCard(id: string): BoardCard | undefined {
    return this.cards.get(id)?.toJSON() as BoardCard | undefined;
  }

  addCard(init: Partial<BoardCard> & Pick<BoardCard, "objectId" | "type">): BoardCard {
    const existing = this.listCards(init.objectId);
    const [w, h] = CARD_SIZE[init.type];
    const now = Date.now();
    const size = { w: init.w ?? w, h: init.h ?? h };
    // asked-for spot (e.g. the middle of the view): nudge it so the new card doesn't cover another one
    const spot = init.x !== undefined && init.y !== undefined ? freeSpot(existing, init.x, init.y, size.w, size.h) : nextCardPosition(existing, init.type);
    const c: BoardCard = {
      id: newId(),
      w, h,
      z: existing.reduce((m, x) => Math.max(m, x.z), 0) + 1,
      author: "",
      createdAt: now,
      updatedAt: now,
      ...init,
      ...spot,
    };
    this.cards.set(c.id, cardMap(c));
    return c;
  }

  updateCard(id: string, patch: Partial<BoardCard>) {
    const m = this.cards.get(id);
    if (!m) return;
    this.doc.transact(() => {
      for (const [k, v] of Object.entries(patch)) if (k !== "id" && k !== "objectId") { if (v === undefined) m.delete(k); else m.set(k, v); }
      m.set("updatedAt", Date.now());
    });
  }

  removeCard(id: string) {
    this.cards.delete(id);
  }

  /** Bring a card to the front. */
  raiseCard(id: string) {
    const c = this.getCard(id);
    if (!c) return;
    const top = this.listCards(c.objectId).reduce((m, x) => Math.max(m, x.z), 0);
    if (c.z < top) this.updateCard(id, { z: top + 1 });
  }

  private removeCardsOf(objectId: string) {
    for (const [id, m] of [...this.cards.entries()]) if (m.get("objectId") === objectId) this.cards.delete(id);
  }

  /** Per object: number of cards and the first image (for the 📌 badge on the map). */
  boardSummary(): Map<string, { count: number; image?: string }> {
    const out = new Map<string, { count: number; image?: string }>();
    for (const c of this.listCards()) {
      const e = out.get(c.objectId) ?? { count: 0 };
      e.count++;
      if (!e.image && c.image) e.image = c.image.hash;
      out.set(c.objectId, e);
    }
    return out;
  }

  /** Every image hash used on any board (for syncing images to the server). */
  imageHashes(): string[] {
    const s = new Set<string>();
    for (const c of this.listCards()) if (c.image) s.add(c.image.hash);
    return [...s];
  }

  /**
   * One-time data upgrades (safe to call often, and by several clients at once):
   * 2-corner buildings → 4-corner footprint polygons. Not recorded in undo history.
   */
  migrate(): number {
    const todo: string[] = [];
    this.objects.forEach((m, id) => {
      const pts = m.get("points") as [number, number][] | undefined;
      if (m.get("kind") === "building" && pts?.length === 2) todo.push(id);
    });
    if (!todo.length) return 0;
    this.doc.transact(() => {
      for (const id of todo) {
        const m = this.objects.get(id)!;
        const pts = m.get("points") as [number, number][];
        if (pts.length === 2) m.set("points", rectCorners(pts[0], pts[1]));
      }
    }, "migrate");
    return todo.length;
  }

  // ---------- files & versions ----------

  /** Everything a .mcplan file holds (plans of all dimensions + shared settings; versions optional). */
  toFile(world: { name?: string; seed?: string } = {}, withVersions = true): PlanFile {
    const meta = this.meta.toJSON() as Record<string, unknown>;
    return {
      format: PLAN_FILE_FORMAT,
      savedAt: Date.now(),
      world: { name: world.name ?? (meta.worldName as string | undefined), seed: world.seed ?? (meta.seed as string | undefined) },
      settings: { home: meta.home as PlanFile["settings"]["home"], kindColors: meta.kindColors as PlanFile["settings"]["kindColors"], palette: meta.palette as string[] | undefined, templates: meta.templates as Template[] | undefined },
      objects: this.list(),
      cards: this.listCards(),
      versions: withVersions ? this.listVersions(true) : undefined,
    };
  }

  /**
   * Load a plan file (or an older JSON export).
   * replace: current objects are replaced (undoable) and the file's shared settings are applied;
   * merge: the file's objects are added (same id = overwritten with the file's version).
   * Versions from the file are added to the version list (never replace existing ones).
   */
  loadFile(data: unknown, mode: "replace" | "merge"): { objects: number; versions: number; cards: number } {
    const f = parsePlanFile(data);
    this.undo.stopCapturing();
    this.doc.transact(() => {
      if (mode === "replace") { this.objects.clear(); this.cards.clear(); }
      for (const o of f.objects) this.objects.set(o.id, toYMap(o));
      for (const c of f.cards ?? []) this.cards.set(c.id, cardMap(c));
    });
    this.undo.stopCapturing();
    let versions = 0;
    this.doc.transact(() => {
      if (mode === "replace" && f.settings) {
        if (f.settings.home) this.meta.set("home", f.settings.home);
        if (f.settings.kindColors) this.meta.set("kindColors", f.settings.kindColors);
        if (f.settings.palette) this.meta.set("palette", f.settings.palette);
      }
      // templates from the file are added (never replace this world's own)
      if (Array.isArray(f.settings?.templates)) {
        const have = new Set(this.templates().map((t) => t.id));
        const add = f.settings.templates.filter((t) => t && typeof t.id === "string" && Array.isArray(t.objects) && !have.has(t.id));
        if (add.length) this.meta.set("templates", [...this.templates(), ...add].slice(0, 100));
      }
      for (const v of f.versions ?? []) {
        if (!v?.id || !Array.isArray(v.objects) || this.versions.has(v.id)) continue;
        this.versions.set(v.id, JSON.stringify(v));
        versions++;
      }
    }, "load");
    return { objects: f.objects.length, versions, cards: f.cards?.length ?? 0 };
  }

  /** @deprecated use toFile — kept for the old "Export JSON" format */
  exportJson() {
    return this.toFile();
  }

  importJson(data: unknown, replace = false) {
    return this.loadFile(data, replace ? "replace" : "merge");
  }

  get versions(): Y.Map<string> {
    return this.doc.getMap<string>("versions");
  }

  /** Named snapshot of all plan objects, stored in the shared doc (everyone in the room sees it). */
  saveVersion(name: string, author = ""): PlanVersion {
    const v: PlanVersion = { id: newId(), name: name.trim() || `Version ${this.versions.size + 1}`, author, createdAt: Date.now(), objects: this.list(), cards: this.listCards() };
    this.doc.transact(() => {
      this.versions.set(v.id, JSON.stringify(v));
      // keep the newest MAX_VERSIONS
      const all = this.listVersions();
      for (const old of all.slice(MAX_VERSIONS)) this.versions.delete(old.id);
    }, "version");
    return v;
  }

  /** Newest first. Without `full`, objects are left out (cheap listing). */
  listVersions(full = false): PlanVersion[] {
    const out: PlanVersion[] = [];
    this.versions.forEach((json) => {
      try {
        const v = JSON.parse(json) as PlanVersion;
        out.push(full ? v : { ...v, objects: [], count: v.objects.length });
      } catch { /* skip broken entry */ }
    });
    return out.sort((a, b) => b.createdAt - a.createdAt);
  }

  getVersion(id: string): PlanVersion | null {
    const j = this.versions.get(id);
    try { return j ? (JSON.parse(j) as PlanVersion) : null; } catch { return null; }
  }

  deleteVersion(id: string) {
    this.versions.delete(id);
  }

  renameVersion(id: string, name: string) {
    const v = this.getVersion(id);
    if (v) this.versions.set(id, JSON.stringify({ ...v, name }));
  }

  /**
   * Replace all plan objects with a version's objects. One undo step (Ctrl+Z brings the
   * current state back); a "Before restoring …" version is saved first as a second safety net.
   */
  restoreVersion(id: string, author = ""): boolean {
    const v = this.getVersion(id);
    if (!v) return false;
    this.saveVersion(`Before restoring “${v.name}”`, author);
    this.undo.stopCapturing();
    this.doc.transact(() => {
      const keep = new Set(v.objects.map((o) => o.id));
      for (const id of [...this.objects.keys()]) if (!keep.has(id)) this.objects.delete(id);
      for (const o of v.objects) {
        const cur = this.objects.get(o.id);
        if (cur && JSON.stringify(cur.toJSON()) === JSON.stringify(o)) continue;
        this.objects.set(o.id, toYMap(o));
      }
      // boards come back too (versions saved before boards existed have no cards: keep today's)
      if (v.cards) {
        const keepC = new Set(v.cards.map((c) => c.id));
        for (const id of [...this.cards.keys()]) if (!keepC.has(id)) this.cards.delete(id);
        for (const c of v.cards) {
          const cur = this.cards.get(c.id);
          if (cur && JSON.stringify(cur.toJSON()) === JSON.stringify(c)) continue;
          this.cards.set(c.id, cardMap(c));
        }
      }
    });
    this.undo.stopCapturing();
    return true;
  }
}

export const PLAN_FILE_FORMAT = "mc-planner/plan@2";
export const MAX_VERSIONS = 50;

export interface PlanVersion {
  id: string;
  name: string;
  author: string;
  createdAt: number;
  objects: PlanObject[];
  cards?: BoardCard[];
  /** listing only: number of objects */
  count?: number;
}

export interface PlanFile {
  format: string;
  savedAt: number;
  world: { name?: string; seed?: string };
  settings: {
    home?: Record<string, HomeLocation>;
    kindColors?: Partial<Record<PlanKind, string>>;
    palette?: string[];
    templates?: Template[];
  };
  objects: PlanObject[];
  cards?: BoardCard[];
  versions?: PlanVersion[];
  /** optional embedded pinboard images: sha256 → data: URL */
  images?: Record<string, string>;
}

const KINDS: PlanKind[] = ["path", "area", "building", "marker"];
export const MATERIAL_RE = /^[a-z0-9_.-]+:[a-z0-9_./-]+$/;

/** "minecraft:dirt_path" → "Dirt Path", "create:andesite_casing" → "Andesite Casing (create)" */
export function materialName(id: string): string {
  const [ns, path] = id.includes(":") ? id.split(":", 2) : ["minecraft", id];
  const name = path.split("/").pop()!.split("_").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");
  return ns === "minecraft" ? name : `${name} (${ns})`;
}

/** Validate/normalise a .mcplan file or an older mc-planner JSON export. Throws a readable error. */
export function parsePlanFile(data: unknown): PlanFile {
  const d = data as Partial<PlanFile> & { meta?: Record<string, unknown> };
  if (!d || typeof d !== "object" || !Array.isArray(d.objects)) throw new Error("This is not an MC Planner plan file.");
  if (typeof d.format === "string" && !/^mc-planner\//.test(d.format)) throw new Error(`Unknown file format “${d.format}”.`);
  const now = Date.now();
  const objects: PlanObject[] = [];
  for (const raw of d.objects) {
    const o = raw as Partial<PlanObject>;
    if (!o || !KINDS.includes(o.kind as PlanKind) || !Array.isArray(o.points)) continue;
    const def = KIND_DEFAULTS[o.kind as PlanKind];
    objects.push({
      id: typeof o.id === "string" && o.id ? o.id : newId(),
      kind: o.kind as PlanKind,
      name: String(o.name ?? def.name),
      color: /^#[0-9a-fA-F]{6}$/.test(o.color ?? "") ? o.color! : def.color,
      status: (["idea", "planned", "in_progress", "done"] as PlanStatus[]).includes(o.status as PlanStatus) ? o.status! : "planned",
      dimension: String(o.dimension ?? "minecraft:overworld"),
      points: o.points.filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])).map((p) => [Math.round(p[0]), Math.round(p[1])] as [number, number]),
      y: Number.isFinite(o.y) ? o.y! : 64,
      width: Number.isFinite(o.width) ? o.width! : def.width,
      height: Number.isFinite(o.height) ? o.height! : def.height,
      ...(Number.isFinite(o.storey) ? { storey: o.storey } : {}),
      ...(typeof o.material === "string" && MATERIAL_RE.test(o.material) ? { material: o.material } : {}),
      ...(typeof o.group === "string" && o.group ? { group: o.group.slice(0, 40), groupName: String(o.groupName ?? "Group").slice(0, 100) } : {}),
      ...(normalizeSourceUrl(o.sourceUrl) ? { sourceUrl: normalizeSourceUrl(o.sourceUrl)! } : {}),
      ...(o.kind === "building" && parseSchematicRef(o.schematic) ? { schematic: parseSchematicRef(o.schematic)! } : {}),
      notes: String(o.notes ?? ""),
      author: String(o.author ?? ""),
      createdAt: Number.isFinite(o.createdAt) ? o.createdAt! : now,
      updatedAt: Number.isFinite(o.updatedAt) ? o.updatedAt! : now,
    });
  }
  const meta = d.meta ?? {};
  return {
    format: PLAN_FILE_FORMAT,
    savedAt: Number.isFinite(d.savedAt) ? d.savedAt! : now,
    world: d.world ?? { name: meta.worldName as string | undefined, seed: meta.seed as string | undefined },
    settings: d.settings ?? { home: meta.home as PlanFile["settings"]["home"], kindColors: meta.kindColors as PlanFile["settings"]["kindColors"], palette: meta.palette as string[] | undefined },
    objects,
    cards: (Array.isArray(d.cards) ? d.cards : []).map((c) => parseCard(c, newId)).filter((c): c is BoardCard => !!c),
    versions: Array.isArray(d.versions) ? d.versions : [],
    images: d.images && typeof d.images === "object" ? Object.fromEntries(Object.entries(d.images).filter(([k, v]) => /^[0-9a-f]{64}$/.test(k) && typeof v === "string" && v.startsWith("data:image/"))) : undefined,
  };
}

function cardMap(c: BoardCard) {
  const m = new Y.Map<unknown>();
  for (const [k, v] of Object.entries(c)) if (v !== undefined) m.set(k, v);
  return m;
}

function toYMap(o: PlanObject) {
  const m = new Y.Map<unknown>();
  for (const [k, v] of Object.entries(o)) if (v !== undefined) m.set(k, v);
  return m;
}

// ---------- geometry helpers (block units) ----------

export function translatePoints(pts: [number, number][], dx: number, dz: number): [number, number][] {
  return pts.map(([x, z]) => [x + dx, z + dz]);
}

/** Rotate block points 90° clockwise (seen from above) around the centre of their bounding box. */
export function rotatePoints90(pts: [number, number][]): [number, number][] {
  const { cx, cz } = bounds(pts);
  // offset so that the rotated shape stays on the block grid
  const ox = Math.floor(cx), oz = Math.floor(cz);
  return pts.map(([x, z]) => [ox - (z - oz), oz + (x - ox)]);
}

export function polylineLength(pts: [number, number][]) {
  let l = 0;
  for (let i = 1; i < pts.length; i++) l += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  return l;
}

export function polygonArea(pts: [number, number][]) {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) a += (pts[j][0] + pts[i][0]) * (pts[j][1] - pts[i][1]);
  return Math.abs(a / 2);
}

export function pointInPolygon(x: number, z: number, pts: [number, number][]) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, zi] = pts[i], [xj, zj] = pts[j];
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

/** Block columns covered by a polygon (corner blocks and edges included). */
export function polygonBlocks(pts: [number, number][]): [number, number][] {
  return gridBlocks(polygonGrid(pts));
}

/** Normalised building footprint from two corner blocks (inclusive). */
export function rectFromCorners(a: [number, number], b: [number, number]) {
  const x0 = Math.min(a[0], b[0]), x1 = Math.max(a[0], b[0]);
  const z0 = Math.min(a[1], b[1]), z1 = Math.max(a[1], b[1]);
  return { x0, z0, x1, z1, w: x1 - x0 + 1, d: z1 - z0 + 1 };
}

/** Footprint corners of a building (old 2-corner buildings → their 4 rectangle corners). */
export function buildingPoints(o: Pick<PlanObject, "kind" | "points">): [number, number][] {
  return o.kind === "building" && o.points.length === 2 ? rectCorners(o.points[0], o.points[1]) : o.points;
}

export const DEFAULT_STOREY = 4;

export interface PlanCounts {
  /** blocks covered: area = columns, path = blocks to pave, building = footprint */
  blocks: number;
  /** bounding size in blocks */
  w: number;
  d: number;
  /** path length (blocks, along the centre line) */
  length?: number;
  /** building: blocks in the one-block wall ring · walls = ring × height */
  ring?: number;
  walls?: number;
  /** building: floor blocks per storey (inside the walls) and number of storeys */
  floor?: number;
  storeys?: number;
  storeyHeight?: number;
  /** building: roof (= footprint) and enclosed volume */
  roof?: number;
  volume?: number;
  /** building shell = walls + floors of the upper storeys + roof */
  shell?: number;
  /** true when the shape was too large to count exactly (area estimate) */
  approx?: boolean;
}

const countCache = new Map<string, PlanCounts>();

/** Block counts for an object (cached by shape). */
export function counts(o: Pick<PlanObject, "kind" | "points" | "width" | "height"> & { storey?: number }): PlanCounts {
  const key = `${o.kind}|${o.width}|${o.height}|${o.storey ?? ""}|${o.points.join(";")}`;
  const hit = countCache.get(key);
  if (hit) return hit;
  const c = computeCounts(o);
  if (countCache.size > 400) countCache.delete(countCache.keys().next().value!);
  countCache.set(key, c);
  return c;
}

function computeCounts(o: Pick<PlanObject, "kind" | "points" | "width" | "height"> & { storey?: number }): PlanCounts {
  const pts = buildingPoints(o);
  if (!pts.length) return { blocks: 0, w: 0, d: 0 };
  const b = bounds(pts);
  const size = { w: b.x1 - b.x0 + 1, d: b.z1 - b.z0 + 1 };
  if (o.kind === "marker") return { blocks: 1, w: 1, d: 1 };
  if (o.kind === "path") {
    const g = pathGrid(pts, Math.max(1, o.width));
    return { blocks: g?.count ?? Math.round(polylineLength(pts) * o.width), ...size, length: Math.round(polylineLength(pts)), approx: !g };
  }
  const g: BlockGrid | null = polygonGrid(pts);
  const blocks = g?.count ?? Math.round(polygonArea(pts));
  if (o.kind === "area") return { blocks, ...size, approx: !g };
  // building
  const h = Math.max(1, o.height);
  const storeyHeight = Math.max(1, o.storey ?? DEFAULT_STOREY);
  const storeys = Math.max(1, Math.floor(h / storeyHeight));
  const ring = g ? ringCount(g) : 0;
  const floor = blocks - ring;
  const walls = ring * h;
  return {
    blocks, ...size, ring, walls, floor, storeys, storeyHeight, roof: blocks, volume: blocks * h,
    shell: walls + floor * (storeys - 1) + blocks, approx: !g,
  };
}

/** One-line summary for lists. */
export function describe(o: PlanObject): string {
  const c = counts(o);
  const n = (v: number) => v.toLocaleString("en-US");
  const mat = o.material ? ` of ${materialName(o.material).toLowerCase()}` : "";
  switch (o.kind) {
    case "path": return `${n(c.length ?? 0)} long · ${o.width} wide · ${n(c.blocks)} blocks${mat}`;
    case "area": return `${c.approx ? "≈" : ""}${n(c.blocks)} blocks${mat} · ${c.w} × ${c.d}`;
    case "building":
      if (o.schematic) { const [w, l] = schematicDims(o.schematic); return `${w} × ${l} · ${o.schematic.size[1]} high · ${n(o.schematic.blocks)} blocks (schematic)`; }
      return `${c.w} × ${c.d} · ${n(c.blocks)} footprint · ${o.height} high${o.material ? ` · ${materialName(o.material)}` : ""}`;
    case "marker": return o.points[0] ? `x ${o.points[0][0]} · y ${o.y} · z ${o.points[0][1]}` : "";
  }
}

